<template>
    <div :class="`file-manager file-manager-${resourceType}`">
        <div v-if="filesLoaded">
            <b-table
                v-if="files.length"
                striped
                hover
                :fields="fields"
                :items="files"
                thead-class="d-none"
            >
                <template v-slot:cell(file_name)="data">
                    <b-link :href="data.item.url" target="_blank">
                        <b-img
                            v-if="data.item.square"
                            :src="data.item.square + '?s=' + data.item.updated"
                            :alt="data.item.file_name"
                            fluid
                            width="35"
                            class="rounded mr-2"
                        ></b-img>
                        {{ data.item.file_name }}
                    </b-link>
                </template>
                <template v-slot:cell(controls)="data">
                    <b-button
                        v-if="data.item.thumb"
                        class="mr-2"
                        variant="primary"
                        size="sm"
                        @click="showPhotoEditor(data.item)"
                        >{{ $t("Edit") }}</b-button
                    >
                    <b-button
                        :disabled="data.item.deleting"
                        variant="danger"
                        size="sm"
                        @click="deleteFile(data.index)"
                        >{{ $t("Delete") }}</b-button
                    >
                </template>
            </b-table>
            <photo-editor
                ref="photo-editor"
                :visible="showPhotoEditor"
                @saved="updateImage"
            ></photo-editor>
            <h5 v-if="title">{{ title }}</h5>
            <vue-dropzone
                id="dropzone"
                ref="filemanager"
                class="rounded"
                @vdropzone-files-added="uploadStarted"
                @vdropzone-success="uploadSuccess"
                @vdropzone-queue-complete="$emit('uploading-finished')"
                :options="dropzoneOptions"
            ></vue-dropzone>
        </div>
        <div v-else>
            {{ $t("Loading files...") }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
.vue-dropzone {
  border: 2px dashed #0d83dd;
  min-height: 0;
  background: url("/images/upload-to-cloud.svg") no-repeat 5% 50%;
  background-size: 64px 64px;

  &:hover {
    background-color: lighten(#0d83dd, 50%);
  }
}
</style>

<script>
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

import axios from "axios";
import _ from "lodash";
import PhotoEditor from "./PhotoEditor";

export default {
  name: "FileManager",
  components: {
    PhotoEditor,
    vueDropzone: vue2Dropzone
  },
  props: {
    resourceType: {
      type: String,
      default: null
    },
    resourceId: {
      type: [String, Number],
      default: null
    },
    title: {
      type: String,
      default: "Upload new files"
    }
  },
  data() {
    return {
      filesLoaded: false,
      fields: [
        { key: "file_name", label: "File Name", class: "align-middle" },
        {
          key: "file_size",
          label: "Size",
          class: "align-middle",
          formatter: value => {
            return this.formatBytes(value);
          }
        },
        {
          key: "controls",
          label: "",
          class: "align-middle text-right"
        }
      ],
      files: [],
      dropzoneOptions: {
        url: this.getUploadUrl,
        thumbnailWidth: 140,
        chunking: false,
        headers: {
          "X-CSRF-TOKEN": document.head.querySelector("[name=csrf-token]")
            .content
        },
        params: {}
      }
    };
  },
  watch: {
    resourceId: _.debounce(function() {
      this.getFiles();
    }, 100),
    resourceType: _.debounce(function() {
      this.getFiles();
    }, 100),
    files() {
      this.$emit("updated-files", this.files);
    }
  },
  mounted() {
    this.getFiles();
  },
  methods: {
    uploadStarted(files) {
      this.$emit("uploading-started", files);
    },
    showPhotoEditor(img) {
      this.$refs["photo-editor"].show(img);
    },
    updateImage(img) {
      let idx = this.files.findIndex(f => f.id === img.id);
      this.$set(this.files, idx, img);
    },
    getUploadUrl() {
      return "/api/file-manager/" + this.resourceType + "/" + this.resourceId;
    },
    uploadSuccess(file, response) {
      this.files.push(response.data);
      this.$refs.filemanager.removeFile(file);
    },
    getFiles() {
      this.loading = true;
      this.filesLoaded = false;
      if (!this.resourceId) {
        this.loading = false;
        this.filesLoaded = true;
        return;
      }
      axios
        .get("/api/file-manager/" + this.resourceType + "/" + this.resourceId)
        .then(response => {
          this.files = response.data.data;
        })
        .finally(() => {
          this.loading = false;
          this.filesLoaded = true;
        });
    },
    formatBytes(a, b) {
      if (0 == a) return "0 Bytes";
      var c = 1024,
        d = b || 2,
        e = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
        f = Math.floor(Math.log(a) / Math.log(c));
      return parseFloat((a / Math.pow(c, f)).toFixed(d)) + " " + e[f];
    },
    deleteFile: function(index) {
      let file = this.files[index];
      this.$set(file, "deleting", true);
      axios.delete("/api/file-manager/" + file.id).then(() => {
        this.$delete(this.files, index);
      });
    }
  }
};
</script>
